if (!customElements.get('product-customization')) {
  customElements.define(
    'product-customization',
    class ProductCustomization extends HTMLElement {
      connectedCallback() {
        this.patternSelect = this.querySelector('[data-cf-pattern]');
        this.colorCountSelect = this.querySelector('[data-cf-color-count]');
        this.fields = {
          customInfo: this.querySelector('[data-cf-field="custom-info"]'),
          colorOption: this.querySelector('[data-cf-field="color-option"]'),
          mainColor: this.querySelector('[data-cf-field="main-color"]'),
          additional1: this.querySelector('[data-cf-field="additional-color-1"]'),
          additional2: this.querySelector('[data-cf-field="additional-color-2"]'),
          topPattern: this.querySelector('[data-cf-field="top-pattern"]'),
        };

        // Custom pattern routes all colour work to email, so the colour-adding
        // extras (wash / hand-painted / drips — the ones with a colour picker) are
        // hidden then. Cache those rows; the physical extras (suction cup, v-lock)
        // stay. product-extras.js excludes `data-cf-pattern-hidden` rows from the
        // selection + subtotal, mirroring its own `data-cf-hidden` rule.
        // Exception: colour extras listed in "custom_keep_extras" (data-cf-custom-keep,
        // e.g. the per-model painting) stay on Custom, only without their picker.
        this.extras = this.querySelector('product-extras');
        const pickerRows = this.extras
          ? Array.from(this.extras.querySelectorAll('[data-cf-extra-row]')).filter((row) =>
              row.querySelector('[data-cf-extra-color]')
            )
          : [];
        this.colorExtraRows = pickerRows.filter((row) => !row.hasAttribute('data-cf-custom-keep'));
        this.customKeepRows = pickerRows.filter((row) => row.hasAttribute('data-cf-custom-keep'));

        // Custom-only extras (e.g. "Add hand painted elements") are the mirror image
        // of the colour extras: shown ONLY while Custom is selected. Rendered hidden
        // by default (data-cf-pattern-hidden), revealed here when the pattern is Custom.
        this.customOnlyRows = this.extras
          ? Array.from(this.extras.querySelectorAll('[data-cf-custom-only]'))
          : [];

        this.addEventListener('change', () => this.update());
        this.update();
      }

      // Show/hide a field AND enable/disable its controls. A disabled control is
      // excluded from the form submission, so hidden fields never send stale
      // values (e.g. "Additional color 2" when only 2 colours are chosen).
      setField(field, visible) {
        if (!field) return;
        field.hidden = !visible;
        field.querySelectorAll('select, input, textarea').forEach((el) => {
          el.disabled = !visible;
        });
      }

      update() {
        const pattern = this.patternSelect ? this.patternSelect.value : '';
        const isCustom = pattern === 'Custom';
        const isSingle = pattern === 'Single color';
        // Surprise: the customer picks only size + firmness; the maker picks the colours.
        const isSurprise = pattern === 'Surprise';
        const isMulti = pattern !== '' && !isCustom && !isSingle && !isSurprise; // Fade / Split / Marble

        this.setField(this.fields.customInfo, isCustom);

        // Colour-adding extras (wash / drips / painting…):
        // - Custom hides them (colour → email), except the custom_keep_extras ones
        //   (e.g. the per-model painting), which stay.
        // - Custom and Surprise have no colour choice, so every remaining picker is
        //   switched off (product-extras.js honours data-cf-color-off for both the
        //   picker and the line's Colour property). On Surprise the maker picks the
        //   extras' colours too.
        if (this.colorExtraRows.length || this.customKeepRows.length) {
          this.colorExtraRows.forEach((row) => row.toggleAttribute('data-cf-pattern-hidden', isCustom));
          [...this.colorExtraRows, ...this.customKeepRows].forEach((row) =>
            row.toggleAttribute('data-cf-color-off', isCustom || isSurprise)
          );
          if (this.extras && typeof this.extras.refresh === 'function') this.extras.refresh();
        }

        // Custom-only extras: the reverse — shown only while Custom is selected.
        if (this.customOnlyRows.length) {
          this.customOnlyRows.forEach((row) => row.toggleAttribute('data-cf-pattern-hidden', !isCustom));
          if (this.extras && typeof this.extras.refresh === 'function') this.extras.refresh();
        }

        this.setField(this.fields.colorOption, isMulti);
        this.setField(this.fields.mainColor, isSingle || isMulti);

        let colorCount = 0;
        if (isSingle) {
          colorCount = 1;
        } else if (isMulti) {
          colorCount = this.colorCountSelect && this.colorCountSelect.value === '3 colors' ? 3 : 2;
        }

        this.setField(this.fields.additional1, colorCount >= 2);
        this.setField(this.fields.additional2, colorCount >= 3);

        // Name each colour field by its PLACEMENT (label + submitted property name),
        // so the customer and the order sheet know where each colour goes. Fade/Split
        // are positional (Tip -> Middle -> Base); Marble sits side by side, so it's
        // just First/Second/Third; Single is one "Main". Applied to the currently
        // shown pickers only. See colorRoles().
        if (colorCount > 0) this.applyColorRoles(pattern, colorCount);

        // Split + 3 colours adds a decision for the top part of the model.
        this.setField(this.fields.topPattern, pattern === 'Split' && colorCount === 3);

        // 3rd-colour surcharge: auto-add its hidden add-on line whenever 3 colours
        // are chosen, remove it otherwise. Rides the existing extras add-to-cart and
        // subtotal via product-extras; refresh only when the state actually flips.
        if (this.extras) {
          const color3 = this.extras.querySelector('[data-cf-color3]');
          if (color3 && !color3.disabled) {
            const want = colorCount === 3;
            if (color3.checked !== want) {
              color3.checked = want;
              if (typeof this.extras.refresh === 'function') this.extras.refresh();
            }
          }

          // Custom-pattern surcharge: auto-add its hidden add-on line whenever the
          // Custom pattern is chosen, remove it otherwise. Same mechanism as the
          // 3rd-colour surcharge above.
          const customSurcharge = this.extras.querySelector('[data-cf-custom-surcharge]');
          if (customSurcharge && !customSurcharge.disabled) {
            if (customSurcharge.checked !== isCustom) {
              customSurcharge.checked = isCustom;
              if (typeof this.extras.refresh === 'function') this.extras.refresh();
            }
          }
        }
      }

      // Placement names for the colour pickers, in order (main -> additional 1 -> 2).
      // Used for BOTH the visible label and the submitted line-item property name, so
      // "Base color: Ducat" reaches the cart, checkout and order. Fade/Split are
      // positional; Marble is side-by-side (position doesn't matter); Single is one.
      // To reword these, edit the strings here.
      colorRoles(pattern, count) {
        if (pattern === 'Single color') return ['Main'];
        if (pattern === 'Marble') {
          return count === 3
            ? ['First color', 'Second color', 'Third color']
            : ['First color', 'Second color'];
        }
        // Fade / Split — positional (top -> bottom)
        return count === 3
          ? ['Tip color', 'Middle color', 'Base color']
          : ['Tip color', 'Base color'];
      }

      applyColorRoles(pattern, count) {
        const roles = this.colorRoles(pattern, count);
        [this.fields.mainColor, this.fields.additional1, this.fields.additional2].forEach((field, i) => {
          const role = roles[i];
          if (!field || !role) return;
          const label = field.querySelector('.cf-label');
          if (label) label.textContent = role;
          const input = field.querySelector('[data-cf-color-input]');
          if (input) input.name = 'properties[' + role + ']';
          const select = field.querySelector('cf-color-select');
          if (select) select.dataset.name = role;
        });
      }
    }
  );
}

// Custom colour dropdown that shows a swatch image per option (markup rendered by
// snippets/cf-color-select.liquid). Writes the chosen colour name into its hidden
// form-associated input, then fires a bubbling `change` so the parent customizer
// (and the extras subtotal) react like they would to a native control.
if (!customElements.get('cf-color-select')) {
  customElements.define(
    'cf-color-select',
    class CfColorSelect extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.cf-cselect__button');
        this.list = this.querySelector('.cf-cselect__list');
        this.input = this.querySelector('[data-cf-color-input]');
        this.valueEl = this.querySelector('[data-cf-cselect-value]');
        this.swatchEl = this.querySelector('[data-cf-cselect-swatch]');
        if (!this.button || !this.list || !this.input) return;

        // Mark the initial option as selected.
        if (this.input.value && window.CSS && CSS.escape) {
          const current = this.list.querySelector(`[data-value="${CSS.escape(this.input.value)}"]`);
          if (current) current.setAttribute('aria-selected', 'true');
        }

        this.button.addEventListener('click', () => this.toggle());
        this.list.addEventListener('click', (event) => {
          const option = event.target.closest('.cf-cselect__option');
          if (option) this.select(option);
        });
        this.onDocClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        document.addEventListener('click', this.onDocClick);
      }

      disconnectedCallback() {
        if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      }

      toggle() {
        if (this.list.hidden) {
          this.open();
        } else {
          this.close();
        }
      }

      open() {
        this.list.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.list.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
      }

      select(option) {
        const value = option.dataset.value || '';
        const image = option.dataset.image || '';

        this.input.value = value;
        if (this.valueEl) this.valueEl.textContent = value;
        if (this.swatchEl) this.swatchEl.style.backgroundImage = image ? `url(${image})` : '';

        this.list.querySelectorAll('[aria-selected]').forEach((el) => el.removeAttribute('aria-selected'));
        option.setAttribute('aria-selected', 'true');

        this.close();
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}
